import { useState } from "react";
import type { ListerProposal, ListerSchema, ListerSchemaAttribute } from "../../../lib/api";

type Vals = ListerProposal["attributes"];
const SINGLE = new Set(["condition"]);

function ChoiceField({ attr, current, onSet }: { attr: ListerSchemaAttribute; current: string[]; onSet: (ids: string[], names: string[]) => void }) {
  const [filter, setFilter] = useState("");
  const single = SINGLE.has(attr.metafield_key ?? "");
  const shown = attr.values.filter((v) => v.name.toLowerCase().includes(filter.toLowerCase()));
  const toggle = (id: string) => {
    const next = single ? (current.includes(id) ? [] : [id]) : current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    onSet(next, next.map((x) => attr.values.find((v) => v.taxonomy_value_id === x)?.name ?? x));
  };
  return (
    <div>
      {attr.values.length > 8 && <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter…" className="mb-1 w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs" />}
      <div className="flex flex-wrap gap-1 max-h-40 overflow-auto">
        {shown.map((v) => (
          <label key={v.taxonomy_value_id} className={`text-xs px-2 py-1 rounded border cursor-pointer ${current.includes(v.taxonomy_value_id) ? "border-green-500 text-green-200" : "border-zinc-800 text-zinc-400"}`}>
            <input type={single ? "radio" : "checkbox"} aria-label={`${attr.name}: ${v.name}`} className="sr-only" checked={current.includes(v.taxonomy_value_id)} onChange={() => toggle(v.taxonomy_value_id)} />
            {v.name}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function AttributeFields({ schema, values, onSet, issues }: { schema: ListerSchema | null; values: Vals; onSet: (key: string, ids: string[], names: string[]) => void; issues: Record<string, string> }) {
  if (!schema) return <div className="text-zinc-500 text-xs">Loading category fields…</div>;
  return (
    <div className="space-y-3 font-mono text-sm">
      {schema.attributes.map((attr) => {
        const key = attr.metafield_key;
        const current = key ? values.filter((v) => v.key === key).flatMap((v) => v.taxonomy_value_ids).filter((id) => attr.values.some((av) => av.taxonomy_value_id === id)) : [];
        return (
          <div key={attr.attribute_id}>
            <div className="flex items-baseline justify-between"><span className="text-zinc-300">{attr.name}</span>{key && issues[`attributes.${key}`] && <span className="text-red-400 text-xs">{issues[`attributes.${key}`]}</span>}</div>
            {!key ? <div className="text-zinc-500 text-xs">set in Shopify admin</div>
              : attr.kind === "choice" ? <ChoiceField attr={attr} current={current} onSet={(ids) => {
                  // attributes sharing a key (Color + Pattern) contribute to one list: keep the other attribute's ids,
                  // then derive names from ids so a colliding value name (e.g. "Clear" on both) can't misalign the arrays.
                  const others = values.filter((v) => v.key === key).flatMap((v) => v.taxonomy_value_ids)
                    .filter((id) => !attr.values.some((av) => av.taxonomy_value_id === id));
                  const mergedIds = [...others, ...ids];
                  const lookup = schema.attributes.filter((a) => a.metafield_key === key).flatMap((a) => a.values);
                  onSet(key, mergedIds, mergedIds.map((id) => lookup.find((av) => av.taxonomy_value_id === id)?.name ?? id));
                }} />
              : <div className="text-zinc-500 text-xs">{attr.options.map((o) => `${o.key}: ${o.value}`).join(" · ") || "measurement"}</div>}
          </div>
        );
      })}
      {schema.unresolved.map((u) => (
        <div key={u.attribute_id}><span className="text-zinc-300">{u.name}</span><div className="text-zinc-500 text-xs">set in Shopify admin ({u.reason})</div></div>
      ))}
    </div>
  );
}
